/**
 * IMPORTS
 */
import { Badge } from "@components/badge/badge";
import type { SecaoDetalhesRemessa } from "@components/modal-detalhes-remessa";

import type { IRemessa } from "@/hooks/expedicoes";

import { formatarDocumento } from "@/utils/format-document";
import { formatarTelefone } from "@/utils/format-telefone";
import { getPrazoProducaoColorClass } from "@/utils/get-prazo-producao-color-class";
import { getSituacaoColorClass } from "@/utils/get-situacao-color-class";

function formatarDataHoraBr(data?: string | null) {
  return data ? new Date(data).toLocaleString("pt-BR") : null;
}

// mesmo texto da coluna "Prazo" das tabelas de tarefas ("1 dia", "3 dias")
function formatarPrazoEmDias(prazo?: number | null) {
  if (prazo === null || prazo === undefined) return null;
  return prazo > 1 ? `${prazo} dias` : `${prazo} dia`;
}

function formatarSimNao(valor?: number | boolean | null) {
  return valor ? "Sim" : "Não";
}

/**
 * Monta as seções do modal de detalhes a partir de uma linha da tabela de expedição.
 * Usa os campos já formatados da linha (status, furo) e a remessa bruta para o restante.
 */
function montarSecoesDetalhesRemessaExpedicao(linha: Record<string, any>): SecaoDetalhesRemessa[] {
  const remessa = (linha.remessaCompleta ?? {}) as Partial<IRemessa>;
  const { cliente, modelo_tecnico: modelo, solicitante, consultor } = remessa;
  const prazoEmDias = formatarPrazoEmDias(remessa.prazo);

  return [
    {
      titulo: "Remessa",
      campos: [
        { label: "Número", value: linha.numero_remessa },
        {
          label: "Status",
          // mesma cor do badge de status da tabela
          value: linha.status ? (
            <Badge className={getSituacaoColorClass(linha.status)}>{linha.status}</Badge>
          ) : null,
        },
        { label: "Quantidade", value: linha.quantidadeSolicitacao },
        {
          label: "Prazo",
          // mesma regra de cor do badge de prazo da tabela
          value: prazoEmDias ? (
            <Badge className={getPrazoProducaoColorClass(prazoEmDias)}>{prazoEmDias}</Badge>
          ) : null,
        },
        { label: "Posição", value: linha.posicao },
        { label: "Data da solicitação", value: linha.data },
        { label: "Início da produção", value: formatarDataHoraBr(remessa.data_inicio_producao) },
      ],
    },
    {
      // quem solicitou a remessa (coluna "Responsável" da tabela)
      titulo: "Solicitante",
      campos: [
        { label: "Nome", value: solicitante?.nome },
        { label: "E-mail", value: solicitante?.email },
        // usuário subordinado do cliente (users_cliente) não tem telefone cadastrado
        ...(solicitante?.telefone
          ? [{ label: "Telefone", value: formatarTelefone(solicitante.telefone) }]
          : []),
        { label: "CPF / CNPJ", value: formatarDocumento(solicitante?.documento ?? "") },
        { label: "Cliente", value: cliente?.nome },
        { label: "E-mail do cliente", value: cliente?.email },
        { label: "Telefone do cliente", value: formatarTelefone(cliente?.telefone ?? "") },
        { label: "CPF / CNPJ do cliente", value: formatarDocumento(cliente?.documento ?? "") },
      ],
    },
    {
      titulo: "Modelo",
      campos: [
        { label: "Modelo", value: modelo?.nome_modelo },
        { label: "Tecnologia", value: linha.tecnologia },
        { label: "Furo", value: linha.furo },
        { label: "Posicionamento", value: modelo?.posicionamento },
        { label: "Campo chave", value: modelo?.campo_chave },
        { label: "Carga de foto", value: modelo ? formatarSimNao(modelo.tem_carga_foto) : null },
        {
          label: "Dados variáveis",
          value: modelo ? formatarSimNao(modelo.tem_dados_variaveis) : null,
        },
      ],
    },
    {
      titulo: "Responsáveis",
      campos: [
        { label: "Consultor", value: consultor?.nome },
        { label: "Designer", value: linha.designer },
      ],
    },
    {
      titulo: "Observação",
      campos: [{ label: "Observação", value: remessa.observacao }],
    },
  ];
}

/**
 * EXPORTS
 */
export { montarSecoesDetalhesRemessaExpedicao };
